import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { api, apiError } from "../../lib/api";

const Field = ({ f, value, onChange }) => {
  if (f.type === "select")
    return (
      <select data-testid={`crud-field-${f.key}`} value={value ?? ""} onChange={(e) => onChange(e.target.value || null)} className="h-11 w-full rounded-xl border border-input bg-white px-3">
        <option value="">—</option>
        {(f.options ?? []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    );
  if (f.type === "checkbox") return <input data-testid={`crud-field-${f.key}`} type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} className="h-5 w-5 accent-[var(--forest)]" />;
  return <Input data-testid={`crud-field-${f.key}`} type={f.type || "text"} value={value ?? ""} required={f.required} onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} className="h-11 rounded-xl" />;
};

export const CrudSection = ({ title, endpoint, fields, defaults, render, testid, onItems }) => {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(undefined);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(endpoint).then((r) => { const l = Array.isArray(r.data) ? r.data : []; setItems(l); onItems?.(l); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [endpoint]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { id, ...body } = edit;
      if (id) await api.put(`${endpoint}/${id}`, body); else await api.post(endpoint, body);
      setEdit(undefined);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };
  const del = async (it) => {
    if (!window.confirm("¿Eliminar?")) return;
    await api.delete(`${endpoint}/${it.id}`).catch((e) => toast.error(apiError(e)));
    load();
  };

  return (
    <div data-testid={testid}>
      <button onClick={() => setEdit({ ...defaults })} data-testid={`${testid}-add`} className="inline-flex items-center gap-2 rounded-full bg-[var(--ink)] px-5 py-3 text-sm font-bold text-white hover:bg-[var(--forest)]"><Plus className="h-4 w-4" /> Agregar {title}</button>
      <div className="mt-5 space-y-3">
        {items === null && <Loader2 className="h-5 w-5 animate-spin" />}
        {items?.length === 0 && <p className="rounded-2xl bg-white p-6 text-[var(--muted-ink)]">Todavía no hay registros.</p>}
        {(items ?? []).map((it) => (
          <div key={it.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white p-5 ring-1 ring-black/5">
            <div className="min-w-0 flex-1 basis-full sm:basis-auto">{render(it)}</div>
            <div className="flex gap-2">
              <button onClick={() => setEdit(it)} data-testid={`${testid}-edit-${it.id}`} className="inline-flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 text-sm font-semibold"><Pencil className="h-4 w-4" /> Editar</button>
              <button onClick={() => del(it)} aria-label="Eliminar" data-testid={`${testid}-delete-${it.id}`} className="rounded-full border border-black/10 p-2.5 text-red-700"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={edit !== undefined} onOpenChange={(o) => !o && setEdit(undefined)}>
        <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto rounded-3xl" data-lenis-prevent>
          <DialogHeader><DialogTitle className="font-display text-3xl">{edit?.id ? "Editar" : "Nuevo"} · {title}</DialogTitle></DialogHeader>
          {edit && (
            <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
              {fields.filter((f) => !f.show || f.show(edit)).map((f) => (
                <label key={f.key} className={f.wide ? "sm:col-span-2" : ""}>
                  <span className="mb-1 block font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--muted-ink)]">{f.label}</span>
                  <Field f={f} value={edit[f.key]} onChange={(v) => setEdit({ ...edit, [f.key]: v })} />
                </label>
              ))}
              <button data-testid={`${testid}-save`} disabled={saving} className="flex h-12 items-center justify-center gap-2 rounded-xl bg-[var(--forest)] font-bold text-white sm:col-span-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Guardar</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
